import React, { useRef, useState, useMemo } from 'react';
import { Space, Button, message, Tag, Typography, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, NodeIndexOutlined } from '@ant-design/icons';
import { permissionApi } from '../../services/manager/permission';
import type { PermissionItem } from '../../types/entity/permission';
import TreeList, { TreeListRef } from '../../components/Templates/Tree';
import { UpsertModal, type FormFieldConfig } from '../../components/Templates/UpsertModal';

const { Text } = Typography;

const statusOptions = [
  { label: '启用', value: 1 },
  { label: '禁用', value: 0 },
];

const Permissions: React.FC = () => {
  const listRef = useRef<TreeListRef>(null);
  const [treeData, setTreeData] = useState<PermissionItem[]>([]);
  
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    record: PermissionItem | null;
    initialValues?: Record<string, any>;
  }>({
    open: false,
    mode: 'create',
    record: null,
  });

  const buildTree = (items: PermissionItem[]) => {
    const nodeMap = new Map<number, PermissionItem & { children?: PermissionItem[] }>();
    items.forEach(item => nodeMap.set(item.id, { ...item, children: [] }));
    const roots: (PermissionItem & { children?: PermissionItem[] })[] = [];
    
    nodeMap.forEach(node => {
      if (node.parent_id && nodeMap.has(node.parent_id)) {
        nodeMap.get(node.parent_id)!.children!.push(node);
      } else {
        roots.push(node);
      }
    });
    
    const sortNodes = (nodes: any[]) => {
      nodes.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
      nodes.forEach(n => {
        if (n.children && n.children.length > 0) sortNodes(n.children);
        else delete n.children;
      });
    };
    sortNodes(roots);
    return roots;
  };

  const fetcher = async () => {
    try {
      const res = await permissionApi.getAll();
      if (res.code === 200 && res.data) {
        const items = res.data.items;
        const tree = buildTree(items);
        setTreeData(tree);
        return tree;
      }
      return [];
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const treeSelectData = useMemo(() => {
    const map = (nodes: any[]): any[] => nodes.map(n => ({
      title: `${n.name} (${n.code})`,
      value: n.id,
      key: n.id,
      children: n.children ? map(n.children) : undefined
    }));
    return map(treeData);
  }, [treeData]);

  const formFields = useMemo<FormFieldConfig[]>(() => [
    { 
      name: 'name', 
      label: '权限名称', 
      type: 'text', 
      required: true, 
      rules: [{ required: true, message: '请输入权限名称' }, { max: 150 }], 
      fieldProps: { placeholder: '如：菜单-账号管理' } 
    },
    { 
      name: 'code', 
      label: '权限编码', 
      type: 'text', 
      required: true, 
      rules: [{ required: true, message: '请输入权限编码' }, { max: 150 }], 
      fieldProps: { placeholder: '如：management.accounts.view' }, 
      disabled: (mode) => mode === 'edit' 
    },
    { 
      name: 'parent_id', 
      label: '父级权限', 
      type: 'tree-select', 
      treeData: treeSelectData,
      fieldProps: { treeDefaultExpandAll: true, allowClear: true }
    },
    { 
      name: 'sort', 
      label: '排序', 
      type: 'number', 
      initialValue: 0, 
      fieldProps: { placeholder: '排序值，越小越靠前' } 
    },
    { 
      name: 'status', 
      label: '状态', 
      type: 'select', 
      options: statusOptions, 
      initialValue: 1 
    },
  ], [treeSelectData]);

  const handleDelete = async (record: PermissionItem) => {
    try {
      const res = await permissionApi.deleteById(String(record.id));
      if (res.code !== 200) {
        throw new Error(res.message || '删除失败');
      }
      message.success('权限已删除');
      listRef.current?.reload();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || '删除失败';
      message.error(msg);
    }
  };

  const beforeSubmit = (values: any) => {
    return {
      ...values,
      parent_id: values.parent_id || undefined
    };
  };

  const renderTitle = (node: PermissionItem) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <Space size="small">
        <Text strong>{node.name}</Text>
        <span style={{ color: '#999' }}>{node.code}</span>
        <Tag color={node.status === 1 ? 'success' : 'default'}>{node.status === 1 ? '启用' : '禁用'}</Tag>
        <span style={{ color: '#999' }}>排序: {node.sort}</span>
      </Space>
      <Space size="small" onClick={(e) => e.stopPropagation()}>
        <Button 
            type="link" 
            icon={<NodeIndexOutlined />} 
            onClick={() => setModalState({ open: true, mode: 'create', record: null, initialValues: { parent_id: node.id } })}
        >
          子权限
        </Button>
        <Button 
          type="link" 
          icon={<EditOutlined />} 
          onClick={() => setModalState({ open: true, mode: 'edit', record: node })}
        >
          编辑
        </Button>
        <Popconfirm 
          title="确定删除该权限？" 
          onConfirm={() => handleDelete(node)} 
          okText="删除" 
          cancelText="取消"
        >
          <Button type="link" icon={<DeleteOutlined />} danger>
            删除
          </Button>
        </Popconfirm>
      </Space>
    </div>
  );

  return (
    <>
      <TreeList<PermissionItem>
        ref={listRef}
        title="权限管理"
        fetcher={fetcher}
        renderTitle={renderTitle}
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setModalState({ open: true, mode: 'create', record: null })}
          >
            新增权限
          </Button>
        }
      />
      <UpsertModal<PermissionItem>
        title={modalState.mode === 'create' ? (modalState.initialValues?.parent_id ? '新增子权限' : '新增权限') : '编辑权限'}
        open={modalState.open}
        mode={modalState.mode}
        record={modalState.record}
        initialValues={modalState.initialValues}
        onCancel={() => setModalState({ ...modalState, open: false })}
        onSuccess={() => {
          setModalState({ ...modalState, open: false });
          listRef.current?.reload();
        }}
        createApi={(data) => permissionApi.create(data)}
        updateApi={(id, data) => permissionApi.updateById(id, data)}
        beforeSubmit={beforeSubmit}
        formFields={formFields}
      />
    </>
  );
};

export default Permissions;
